/**
 * API Service - Wrapper pour les appels API avec authentification
 * Gère automatiquement le refresh token en cas d'erreur 401
 */

import { getAuthBaseUrl, getServerDomain } from '@src/utility/serverUrls'

const SERVERS = ['input', 'global', 'client', 'consult', 'analysis', 'simulation', 'user']

/**
 * Erreur API avec statut et corps de réponse
 */
class ApiError extends Error {
    constructor(message, status, responseBody) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.responseBody = responseBody
    }
}

/**
 * Initialise les URLs des APIs en sessionStorage si elles ne sont pas définies
 * (même logique que common.js getURLsAPIs)
 */
export const ensureApiUrls = () => {
    if (sessionStorage.getItem('url-consult')) return

    const urlServer = getServerDomain()

    for (const srv of SERVERS) {
        sessionStorage.setItem(`url-${srv}`, `https://${srv}.${urlServer}`)
    }
}

/**
 * Récupère les données client depuis l'API consult et stocke le branding + les devises gérées
 */
export const fetchManagedCurrencies = async () => {
    const consultUrl = sessionStorage.getItem('url-consult')
    if (!consultUrl) return

    try {
        const response = await fetch(`${consultUrl}/client`, {
        headers: {
            Authorization: 'Bearer '.concat(JSON.parse(localStorage.getItem('accessToken') || 'null')),
            'Content-Type': 'application/x-www-form-urlencoded'
        }
        })
        if (!response.ok) return

        const client = await response.json()

        if (client?.managedcurrencies && client.managedcurrencies.trim() !== '') {
            const value = client.managedcurrencies.replaceAll('§', '"')
            localStorage.setItem('managedcurrencies', value)
        }
        // la devise pivot : tout montant est stocké et saisi dans cette devise, les montants lus sont affichés
        // dans la devise de l'utilisateur (getPivotCurrency de public/common.js)
        localStorage.setItem('pivotCurrency', client?.currency || 'EUR')

        const { name, shortname, flagimage, flagimagedark, background } = client
        if (name || shortname || flagimage) {
            localStorage.setItem('client', JSON.stringify({ name, shortname, flagimage: flagimage || '', flagimagedark: flagimagedark || '', background: background || '' }))
            window.dispatchEvent(new Event('clientIdentityLoaded'))
        }
    } catch (err) {
        console.warn('Erreur lors du chargement des données client:', err)
    }
}

/**
 * Vérifie et rafraîchit le token d'accès si nécessaire
 * @returns {Promise<void>}
 */
const checkAndRefreshAccessToken = async () => {
    try {
        const baseUrl = getAuthBaseUrl()

        const response = await fetch(`${baseUrl}/token`, {
            headers: {
                Authorization: `Bearer ${JSON.parse(localStorage.getItem('accessToken'))}`,
                'x-refresh-token': JSON.parse(localStorage.getItem('refreshToken')),
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        })

        if (response.ok) {
            const data = await response.json()
            localStorage.setItem('accessToken', JSON.stringify(data.accessToken))
            localStorage.setItem('refreshToken', JSON.stringify(data.refreshToken))
        } else {
            // Si le refresh échoue, rediriger vers la page de login
            console.error('Token refresh failed, redirecting to login')
            window.location.href = '/login'
        }
    } catch (error) {
        console.error('Error refreshing token:', error)
        window.location.href = '/login'
    }
}

/**
 * Effectue un appel fetch avec gestion automatique du refresh token
 * @param {string} url - URL de l'API
 * @param {object} options - Options du fetch
 * @returns {Promise<Response>}
 */
const fetchWithAuth = async (url, options) => {
    const response = await fetch(url, options)
    
    // Si 401, refresh le token et réessayer
    if (response.status === 401) {
        await checkAndRefreshAccessToken()
        
        // Mettre à jour le header Authorization avec le nouveau token
        const updatedHeaders = {
            ...options.headers,
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('accessToken'))}`
        }
        
        const updatedOptions = {
            ...options,
            headers: updatedHeaders
        }
        
        // Réessayer la requête
        return await fetch(url, updatedOptions)
    }
    
    return response
}

/**
 * Effectue un appel GET avec authentification
 * @param {string} url - URL complète de l'API
 * @param {object} options - Options supplémentaires (headers, etc.)
 * @returns {Promise<any>} - Données de la réponse
 */
export const apiGet = async (url, options = {}) => {
    const defaultOptions = {
        headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options.headers
        }
    }

    const response = await fetchWithAuth(url, {
        ...options,
        ...defaultOptions
    })

    if (!response.ok) {
        const body = await response.text()
        throw new ApiError(`API Error: ${response.status} ${response.statusText}`, response.status, body)
    }

    return response.json()
}

/**
 * Effectue un appel POST avec authentification
 * @param {string} url - URL complète de l'API
 * @param {object} data - Données à envoyer
 * @param {object} options - Options supplémentaires
 * @returns {Promise<any>} - Données de la réponse
 */
export const apiPost = async (url, data = {}, options = {}) => {
    const defaultOptions = {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options.headers
        }
    }

    const response = await fetchWithAuth(url, {
        ...options,
        ...defaultOptions
    })

    if (!response.ok) {
        const body = await response.text()
        throw new ApiError(`API Error: ${response.status} ${response.statusText}`, response.status, body)
    }

    return response.json()
}

/**
 * Effectue un appel PUT avec authentification
 * @param {string} url - URL complète de l'API
 * @param {object} data - Données à envoyer
 * @param {object} options - Options supplémentaires
 * @returns {Promise<any>} - Données de la réponse
 */
export const apiPut = async (url, data = {}, options = {}) => {
    const defaultOptions = {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options.headers
        }
    }

    const response = await fetchWithAuth(url, {
        ...options,
        ...defaultOptions
    })

    if (!response.ok) {
        const body = await response.text()
        throw new ApiError(`API Error: ${response.status} ${response.statusText}`, response.status, body)
    }

    return response.json()
}

/**
 * Effectue un appel DELETE avec authentification
 * @param {string} url - URL complète de l'API
 * @param {object} options - Options supplémentaires
 * @returns {Promise<any>} - Données de la réponse
 */
export const apiDelete = async (url, options = {}) => {
    const defaultOptions = {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options.headers
        }
    }

    const response = await fetchWithAuth(url, {
        ...options,
        ...defaultOptions
    })

    if (!response.ok) {
        const body = await response.text()
        throw new ApiError(`API Error: ${response.status} ${response.statusText}`, response.status, body)
    }

    return response.json()
}

/**
 * Récupère les URLs des APIs depuis sessionStorage
 * @returns {object} - URLs des APIs
 */
export const getApiUrls = () => ({
    consult: sessionStorage.getItem('url-consult'),
    client: sessionStorage.getItem('url-client'),
    analysis: sessionStorage.getItem('url-analysis'),
    input: sessionStorage.getItem('url-input'),
    user: sessionStorage.getItem('url-user'),
    global: sessionStorage.getItem('url-global')
})

/**
 * Récupère les informations utilisateur depuis localStorage
 * @returns {object} - Informations utilisateur
 */
export const getUserInfo = () => {
    try {
        return {
            accessToken: JSON.parse(localStorage.getItem('accessToken')),
            refreshToken: JSON.parse(localStorage.getItem('refreshToken')),
            userData: JSON.parse(localStorage.getItem('userData')),
            userCurrency: localStorage.getItem('userCurrency') || localStorage.getItem('pivotCurrency') || 'EUR',
            offering: sessionStorage.getItem('offering')
        }
    } catch (error) {
        console.error('Error getting user info:', error)
        return {
            accessToken: null,
            refreshToken: null,
            userData: null,
            userCurrency: localStorage.getItem('pivotCurrency') || 'EUR',
            offering: null
        }
    }
}

/**
 * Client API par défaut - Interface simplifiée
 */
const apiClient = {
    get: apiGet,
    post: apiPost,
    put: apiPut,
    delete: apiDelete,
    urls: getApiUrls,
    user: getUserInfo
}

export default apiClient

